import React, { useEffect, useState } from "react";
import { UserPlus, Loader2, MailCheck, MailWarning, MailX, AlertTriangle, Smartphone } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
const ADMIN_LEVEL = ["Admin", "President", "General Secretary", "Vice President"];
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const ROLES = ["member", "Admin", "President", "Vice President", "General Secretary", "Assistant General Secretary", "Treasurer", "Financial Secretary", "Welfare Officer", "Organizer", "Assistant Organizer", "PRO", "Cultural Officer", "Internal Auditor", "Chaplain", "Trustee"];
const LABEL = { member: "Member", PRO: "Public Relations Officer (PRO)" };
const EMPTY = { email: "", first_name: "", last_name: "", phone: "", member_id: "", join_date: "", dob: "", address: "", lineage_status: "associate", role: "member", opening_balance: "", grandfathered: false, status: "active", send_invite: true, send_sms: true };
const SMS_MSG = { sent: "Welcome SMS with the link sent.", queued_unconfigured: "SMS queued — Twilio sender number not configured yet.", skipped_no_phone: "No phone number, so no SMS.", failed: "SMS failed to send.", held: "" };

function DuplicateWarning({ f }) {
  const [matches, setMatches] = useState([]);
  useEffect(() => {
    const name = `${f.first_name} ${f.last_name}`.trim();
    if (name.length < 3 && f.phone.length < 7 && !f.email.includes("@")) { setMatches([]); return undefined; }
    const id = setTimeout(async () => {
      try { const { data } = await api.get("/admin/member-tools/check-duplicates", { params: { name, phone: f.phone, email: f.email } }); setMatches(data); } catch { setMatches([]); }
    }, 450);
    return () => clearTimeout(id);
  }, [f.first_name, f.last_name, f.phone, f.email]);
  if (matches.length === 0) return null;
  return (
    <div className="rounded-xl border border-amber-400 bg-amber-50 p-3 text-sm" data-testid="duplicate-warning">
      <p className="flex items-center gap-2 font-semibold text-amber-900"><AlertTriangle size={16} /> Possible duplicate — {matches.length} existing account{matches.length > 1 ? "s" : ""} look similar</p>
      <ul className="mt-2 space-y-1">
        {matches.map((m) => (
          <li key={m.id} className="flex flex-wrap items-center gap-2 text-amber-900" data-testid={`duplicate-${m.id}`}>
            <span className="font-semibold">{m.name}</span><span className="font-mono-accent text-xs">{m.member_id}</span><span className="text-xs">{m.email}{m.phone ? ` · ${m.phone}` : ""}</span>
            <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-bold uppercase">{m.reasons.join(", ")}</span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-amber-800">Double-check before creating — you may want to update the existing account instead.</p>
    </div>
  );
}

const INVITE = { sent: [MailCheck, "text-palm", "Invite email sent — they can set a password from the link."], held: [MailWarning, "text-gold", "No invite sent. Use “Resend invite” on the member row when ready."], failed: [MailX, "text-destructive", "Invite could not be delivered — check the email address."], "n/a": [MailWarning, "text-muted-foreground", "Record-only member (no email) — no login/invite."] };

function Field({ label, k, f, set, type = "text", placeholder, testId }) {
  return <div><Label>{label}</Label><Input type={type} placeholder={placeholder} value={f[k]} onChange={(e) => set({ ...f, [k]: e.target.value })} data-testid={testId || `add-member-${k}`} /></div>;
}

function AddMemberResult({ result, onReset, onDone }) {
  const [Icon, color, msg] = INVITE[result.invite] || [];
  return (
    <div className="space-y-4" data-testid="add-member-result">
    <div className="rounded-xl border border-palm/40 bg-palm/5 p-4">
      <p className="font-semibold">{result.name} <span className="font-mono-accent text-xs text-gold">{result.member_id}</span></p>
      <p className="text-sm text-muted-foreground">{result.email} · {result.role} · {result.status}</p>
      {result.opening_balance != null && <p className="text-sm">Opening balance set to ${result.opening_balance.toFixed(2)}</p>}
      <p className={`mt-2 flex items-center gap-2 text-sm ${color}`} data-testid="add-member-invite-status"><Icon size={16} /> {msg}</p>
      {SMS_MSG[result.sms] && <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground" data-testid="add-member-sms-status"><Smartphone size={16} /> {SMS_MSG[result.sms]}</p>}
    </div>
    <div className="flex gap-2">
      <Button onClick={onReset} variant="outline" className="flex-1" data-testid="add-member-another">Add another</Button>
      <Button onClick={onDone} className="flex-1 bg-crimson" data-testid="add-member-done">Done</Button>
    </div>
            </div>
  );
}

function AddMemberForm({ f, set, isAdminLevel, busy, onSubmit }) {
  return (
    <div className="space-y-4">
    <div className="grid gap-3 sm:grid-cols-2">
      <Field label="First name *" k="first_name" f={f} set={set} />
      <Field label="Last name *" k="last_name" f={f} set={set} />
      <Field label="Email (login) — blank = record only, no login" k="email" f={f} set={set} type="email" />
      <Field label="Phone" k="phone" f={f} set={set} placeholder="+1 508 555 0100" />
      <Field label="Member ID" k="member_id" f={f} set={set} placeholder="Auto-generated if blank" />
      <Field label="Join date" k="join_date" f={f} set={set} type="date" />
      <Field label="Date of birth" k="dob" f={f} set={set} type="date" />
      <div><Label>Lineage</Label>
        <Select value={f.lineage_status} onValueChange={(v) => set({ ...f, lineage_status: v })}>
          <SelectTrigger data-testid="add-member-lineage"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="lineage">Lineage</SelectItem><SelectItem value="associate">Associate</SelectItem><SelectItem value="royal">Royal</SelectItem></SelectContent>
        </Select></div>
    </div>
    <Field label="Address" k="address" f={f} set={set} />
    <div className="grid gap-3 sm:grid-cols-3">
      <div><Label>Role / access</Label>
        <Select value={f.role} onValueChange={(v) => set({ ...f, role: v })} disabled={!isAdminLevel}>
          <SelectTrigger data-testid="add-member-role"><SelectValue /></SelectTrigger>
          <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{LABEL[r] || r}</SelectItem>)}</SelectContent>
        </Select>
        {!isAdminLevel && <p className="mt-1 text-[11px] text-muted-foreground">Only Admin/President/VP can assign officer roles.</p>}
      </div>
      <div><Label>Account status</Label>
        <Select value={f.status} onValueChange={(v) => set({ ...f, status: v })}>
          <SelectTrigger data-testid="add-member-status"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="active">Active (can sign in)</SelectItem><SelectItem value="pending">Pending (blocked until approved)</SelectItem></SelectContent>
        </Select></div>
      <Field label="Opening balance ($)" k="opening_balance" f={f} set={set} placeholder="e.g. 120" />
    </div>
    <div className="flex flex-wrap gap-6 rounded-xl border border-gold/30 bg-gold-light/40 p-3">
      <label className="flex items-center gap-2 text-sm"><Switch checked={f.grandfathered} onCheckedChange={(v) => set({ ...f, grandfathered: v })} data-testid="add-member-grandfathered" /> Grandfathered (no registration fee, immediate welfare)</label>
      <label className="flex items-center gap-2 text-sm"><Switch checked={f.send_invite} onCheckedChange={(v) => set({ ...f, send_invite: v })} data-testid="add-member-invite" /> Email set-password invite now</label>
      <label className="flex items-center gap-2 text-sm"><Switch checked={f.send_sms} disabled={!f.send_invite} onCheckedChange={(v) => set({ ...f, send_sms: v })} data-testid="add-member-sms" /> Also text the link (SMS)</label>
    </div>
    <DuplicateWarning f={f} />
    <Button onClick={onSubmit} disabled={busy || !f.first_name || !f.last_name} className="w-full bg-crimson" data-testid="add-member-submit">
      {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <UserPlus size={16} className="mr-2" />} {f.email ? "Create account" : "Create record (no login)"}
    </Button>
            </div>
  );
}

export function AddMember({ onCreated }) {
  const { user: me } = useAuth();
  const isAdminLevel = ADMIN_LEVEL.includes(me?.role);
  const [open, setOpen] = useState(false);
  const [f, set] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/member-tools/create", f); setResult(data); toast.success(`${data.name} added as ${data.member_id}`); onCreated?.(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const reset = () => { set(EMPTY); setResult(null); };
  return (
    <>
      <Button onClick={() => setOpen(true)} className="gap-2 bg-crimson" data-testid="add-member-btn"><UserPlus size={16} /> Add member</Button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="add-member-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-crimson">Add a member</DialogTitle></DialogHeader>
          {result ? <AddMemberResult result={result} onReset={reset} onDone={() => setOpen(false)} /> : <AddMemberForm f={f} set={set} isAdminLevel={isAdminLevel} busy={busy} onSubmit={submit} />}
        </DialogContent>
      </Dialog>
    </>
  );
}
